// Container weighing and send-out forms (Stage 6 slice 3, plan §D). Pure: no Vue, no API calls,
// so node:test can load it. The weighing fields reuse the Stage 1 form (weighingForm.mjs); the
// tare is filled in from the container. The API checks everything again
// (api/core/compliance/waste/{weighings,sendOuts}.js); these checks only keep Save disabled and
// show hints.
import {
  buildWeighingBody,
  emptyWeighingForm,
  weighingErrors,
} from "../Acceptance/weighingForm.mjs";

export const TEXT_MAX = 200; // recipient and transporter, as the API
export const FILE_MAX_BYTES = 15 * 1024 * 1024;
export const FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

const ROME_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Rome",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// The Europe/Rome calendar day of an instant: 'YYYY-MM-DD'.
export function romeDay(instant) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(
    ROME_DAY.formatToParts(date).map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const time = (value) => (value ? new Date(value).getTime() : 0);

// A weighing form for the container: weighbridge by default, the tare from the container.
// tareFromContainer tells the dialog to say "dal contenitore".
export function containerWeighingForm(container, now = new Date()) {
  const tare =
    container && container.tare != null ? String(container.tare) : "";
  return {
    ...emptyWeighingForm(now),
    method: "gross_tare",
    tare,
  };
}

export function tareFromContainer(form, container) {
  return Boolean(
    container &&
      container.tare != null &&
      form &&
      form.method === "gross_tare" &&
      String(form.tare).trim() === String(container.tare),
  );
}

// The container's latest weighing as the send-out offers it: null when there is none; fresh is
// false when it was taken before the last entry (D6: weigh again).
export function latestWeighingOption(container) {
  const latest = container && container.latestWeighing;
  if (!latest) return null;
  return {
    id: latest.id,
    netKg: latest.netKg,
    weighedAt: latest.weighedAt,
    fresh: latest.fresh === true,
  };
}

// A new send-out: the latest weighing when it is fresh, otherwise a new weighing; today's date.
export function emptySendOutForm(container, now = new Date()) {
  const latest = latestWeighingOption(container);
  return {
    source: latest && latest.fresh ? "latest" : "new",
    weighing: containerWeighingForm(container, now),
    date: romeDay(now),
    recipient: "",
    transporter: "",
    file: null,
  };
}

export function checkFile(file) {
  if (!file) return null;
  if (file.size > FILE_MAX_BYTES) return "too_large";
  if (!FILE_TYPES.includes(file.type)) return "unsupported_type";
  return null;
}

// { field: reason } for every problem; empty when the form can be sent. Weighing fields are
// "weighing.<field>". Reasons: required, too_long, invalid_date, future_date, before_filling,
// weighing_stale, too_large, unsupported_type and the weighingForm reasons.
export function sendOutErrors(form, container, now = new Date()) {
  const errors = {};
  if (isBlank(form.recipient)) errors.recipient = "required";
  else if (form.recipient.trim().length > TEXT_MAX)
    errors.recipient = "too_long";
  if (!isBlank(form.transporter) && form.transporter.trim().length > TEXT_MAX)
    errors.transporter = "too_long";

  const filling = (container && container.filling) || {};
  if (isBlank(form.date)) errors.date = "required";
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) errors.date = "invalid_date";
  else if (form.date > romeDay(now)) errors.date = "future_date";
  else if (filling.openedAt && form.date < romeDay(filling.openedAt))
    errors.date = "before_filling";

  if (form.source === "latest") {
    const latest = latestWeighingOption(container);
    if (!latest) errors.weighing = "required";
    else if (!latest.fresh) errors.weighing = "weighing_stale";
  } else {
    const weighing = form.weighing || {};
    const weighingProblems = weighingErrors(weighing, now, {
      ticketRequired: false,
    });
    Object.entries(weighingProblems).forEach(([field, reason]) => {
      errors[`weighing.${field}`] = reason;
    });
    if (
      !errors["weighing.weighedAt"] &&
      filling.lastEntryAt &&
      time(weighing.weighedAt) <= time(filling.lastEntryAt)
    ) {
      errors["weighing.weighedAt"] = "weighing_stale";
    }
  }
  const fileProblem = checkFile(form.file);
  if (fileProblem) errors.file = fileProblem;
  return errors;
}

// The send-out for recordWasteSendOut: { date, recipient, transporter?, weighingId | weighing,
// file? }.
export function buildSendOut(form, container) {
  const body = {
    date: form.date,
    recipient: form.recipient.trim().replace(/\s+/g, " "),
  };
  if (!isBlank(form.transporter))
    body.transporter = form.transporter.trim().replace(/\s+/g, " ");
  if (form.source === "latest") {
    body.weighingId = latestWeighingOption(container).id;
  } else {
    body.weighing = buildWeighingBody(form.weighing);
  }
  if (form.file) body.file = form.file;
  return body;
}

// The form field a server error belongs to: the code first, then details.field.
export function serverErrorField(data) {
  const code = data && data.code;
  if (code === "recipient_required") return "recipient";
  if (code === "weight_required") return "weighing";
  if (code === "weighing_stale") {
    return data.details && data.details.field === "weighingId"
      ? "weighing"
      : "weighing.weighedAt";
  }
  const field = data && data.details && data.details.field;
  if (!field) return null;
  return [
    "method",
    "gross",
    "tare",
    "net",
    "ticketNumber",
    "device",
    "weighedAt",
  ].includes(field)
    ? `weighing.${field}`
    : field;
}
